import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import { maxCharLimitExceededError, exceedsMax,sliceMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { CriminalWizardActions } from './CriminalWizardActions';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { criminalWizardPath, CriminalWizardShell, FieldError, inpClass } from './CriminalWizardShell';
import { isBriberySubCategory, isOtherLookup } from './criminalConstants';
import { useCriminalLookups } from './useCriminalLookups';
import { useCaseCategorization } from './useCaseCategorization';
import { appendInstituteValidationSpecs } from '../../../utils/instituteTypeValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function CriminalStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useCriminalLookups('step1', draft.provinceId, draft.districtId);
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const specs: RequiredFieldSpec[] = [
      { key: 'fileCategoryId', label: f.fileCategory, value: draft.fileCategoryId, elementId: 'crim-fileCategory' },
      { key: 'subFileCategoryId', label: f.subFileCategory, value: draft.subFileCategoryId, elementId: 'crim-subFileCategory' },
      { key: 'caseSourceId', label: f.caseSource, value: draft.caseSourceId, elementId: 'crim-caseSource' },
      { key: 'provinceId', label: f.province, value: draft.provinceId, elementId: 'crim-province' },
      { key: 'districtId', label: f.district, value: draft.districtId, elementId: 'crim-district' },
      { key: 'magistrateCourtId', label: f.magistrateCourtName, value: draft.magistrateCourtId, elementId: 'crim-magistrateCourt' },
      { key: 'policeStationId', label: f.policeStation, value: draft.policeStationId, elementId: 'crim-policeStation' },
      { key: 'yourReferenceNumber', label: f.yourReferenceNumber, value: draft.yourReferenceNumber, elementId: 'crim-yourRef' },
      { key: 'hasConnectedMatters', label: f.connectedMatters, value: draft.hasConnectedMatters, elementId: 'crim-connected' },
    ];
    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: f.connectedCaseNumber, value: draft.connectedCaseNumber, elementId: 'crim-connectedCase' },
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push(
          { key: 'connectedAgdFileRef', label: f.connectedAgdFileRef, value: draft.connectedAgdFileRef, elementId: 'crim-connectedAgd'})
      }
    }
    if (isOtherLookup(draft.caseSource, lookups.caseSources.find((s) => s.id === draft.caseSourceId)?.code)) {
      specs.push({ key: 'caseSourceOther', label: 'Case Source (Other)', value: draft.caseSourceOther, elementId: 'crim-caseSourceOther' });
    }

    appendInstituteValidationSpecs(
      specs,
      {
        instituteType: draft.instituteType,
        corporateInstituteId: draft.corporateInstitute,
        corporateInstituteName: draft.corporateInstituteName,
        corporateInstituteOther: draft.corporateInstituteOther,
        corporateInstituteContactName: draft.corporateInstituteContactName,
        corporateInstitutePrefix: draft.corporateInstitutePrefix },
      draft.fileCategoryId,
      cat.fileCategories,
      'crim',
      lookups.corporateInstitutes,
    );

    const reqErrs = validateRequired(specs);
    const extra: RequiredErrors = {};
    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
    if (exceedsMax(draft.corporateInstituteOther, MAX_REF)) extra.corporateInstituteOther = maxCharLimitExceededError();
    const all = { ...reqErrs, ...extra };
    setErrors(all);
    if (Object.keys(all).length) {
      focusFirstInvalid(specs, all);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    nav(criminalWizardPath(2, draft.draftRequestId));
  };

  const caseSourceIsOther = useMemo(() => {
    const item = lookups.caseSources.find((s) => s.id === draft.caseSourceId);
    return isOtherLookup(draft.caseSource, item?.code ?? draft.caseSourceId);
  }, [draft.caseSource, draft.caseSourceId, lookups.caseSources]);

  const showHighCourt = useMemo(
    () => isBriberySubCategory(draft.subFileCategoryName || ''),
    [draft.subFileCategoryName],
  );

  useEffect(() => {
    if (!showHighCourt && draft.highCourtId) {
      setDraft((d) => ({ ...d, highCourtId: '' }));
    }
  }, [draft.highCourtId, setDraft, showHighCourt]);

  useEffect(() => {
    const hasRef = Boolean(draft.connectedAgdFileRef?.trim());
    const hasCaseNum = Boolean(draft.connectedCaseNumber?.trim());
    if (!hasRef && !hasCaseNum) return;
    if (draft.hasConnectedMatters === 'yes' && (!hasRef || draft.connectedAgdFileRefAvailable)) return;
    setDraft((d) => ({
      ...d,
      hasConnectedMatters: 'yes',
      connectedAgdFileRefAvailable: hasRef ? true : d.connectedAgdFileRefAvailable,
    }));
  }, [
    draft.connectedAgdFileRef,
    draft.connectedCaseNumber,
    draft.hasConnectedMatters,
    draft.connectedAgdFileRefAvailable,
    setDraft,
  ]);


  return (
    <CriminalWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.description}</label>
          <textarea
            id="crim-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.caseSource} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-caseSource"
              hasError={Boolean(errors.caseSourceId)}
              value={draft.caseSourceId}
              placeholder={f.sourceOfCase}
              options={toOpts(lookups.caseSources)}
              onChange={(id) => {
                const name = lookups.caseSources.find((s) => s.id === id)?.name ?? '';
                setDraft((d) => ({
                  ...d,
                  caseSourceId: id,
                  caseSource: name,
                  caseSourceOther: isOtherLookup(name, lookups.caseSources.find((s) => s.id === id)?.code)
                    ? d.caseSourceOther
                    : '' }));
                clearErr('caseSourceId');
              }}
            />
            <FieldError message={errors.caseSourceId} />
          </div>

          {caseSourceIsOther ? (
            <div className="ccr-fg">
              <label>
                Case Source (Other) <span className="req">*</span>
              </label>
              <input
                id="crim-caseSourceOther"
                className={inpClass(Boolean(errors.caseSourceOther))}
                type="text"
                value={draft.caseSourceOther}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, caseSourceOther: maxLen('caseSourceOther', e.target.value, MAX_REF) }));
                }}
                placeholder={f.caseSourceOther}
              />
              <FieldError message={errors.caseSourceOther} />
            </div>
          ) : (
            <div />
          )}
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={toOpts(lookups.provinces)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '',
                  magistrateCourtId: '',
                  magistrateCourtName: '',
                  highCourtId: '',
                  highCourtName: '',
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('provinceId');
              }}
              onClear={() => {
                setDraft((d) => ({
                  ...d,
                  provinceId: '',
                  provinceName: '',
                  districtId: '',
                  districtName: '',
                  magistrateCourtId: '',
                  magistrateCourtName: '',
                  highCourtId: '',
                  highCourtName: '',
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>

          <div className="ccr-fg">
            <label>
              {f.district} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-district"
              hasError={Boolean(errors.districtId)}
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={toOpts(lookups.districts)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id),
                  magistrateCourtId: '',
                  magistrateCourtName: '',
                  highCourtId: '',
                  highCourtName: '',
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('districtId');
              }}
            />
            <FieldError message={errors.districtId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.magistrateCourtName} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-magistrateCourt"
              hasError={Boolean(errors.magistrateCourtId)}
              value={draft.magistrateCourtId}
              disabled={!draft.districtId}
              placeholder={f.selectMagistrateCourt}
              options={toOpts(lookups.magistrateCourts)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  magistrateCourtId: id,
                  magistrateCourtName: lookupName(lookups.magistrateCourts, id) }));
                clearErr('magistrateCourtId');
              }}
            />
            <FieldError message={errors.magistrateCourtId} />
          </div>

          {showHighCourt ? (
            <div className="ccr-fg">
              <label>{f.highCourtName}</label>
              <WizardSelect
                id="crim-highCourt"
                value={draft.highCourtId}
                disabled={!draft.districtId}
                placeholder={f.selectHighCourt}
                options={toOpts(lookups.highCourts)}
                onChange={(id) =>
                  setDraft((d) => ({
                    ...d,
                    highCourtId: id,
                    highCourtName: lookupName(lookups.highCourts, id) }))
                }
              />
            </div>
          ) : (
            <div />
          )}
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.policeStation} <span className="req">*</span>
            </label>
            <WizardSelect
              id="crim-policeStation"
              hasError={Boolean(errors.policeStationId)}
              value={draft.policeStationId}
              disabled={!draft.districtId}
              placeholder={f.selectPoliceStation}
              options={toOpts(lookups.policeStations)}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  policeStationId: id,
                  policeStationName: lookupName(lookups.policeStations, id) }));
                clearErr('policeStationId');
              }}
            />
            <FieldError message={errors.policeStationId} />
          </div>
          <div />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="crim-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d) => ({ ...d, yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) => setDraft((d) => ({ ...d, otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))}
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: draft.hasConnectedMatters === 'yes' ? 14 : 0 }}>
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              id="crim-caseNumber"
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"
              value={draft.caseNumber}
              onChange={(e) => setDraft((d) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }))}
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>

          <div className="ccr-fg" id="crim-connected">
            <label>
              {f.connectedMatters} <span className="req">*</span>
            </label>
            <div className="ccr-radio-row">
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connected"
                  checked={draft.hasConnectedMatters === 'yes'}
                  onChange={() => {
                    setDraft((d) => ({ ...d, hasConnectedMatters: 'yes' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.yes}</span>
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connected"
                  checked={draft.hasConnectedMatters === 'no'}
                  onChange={() => {
                    setDraft((d) => ({
                      ...d,
                      hasConnectedMatters: 'no',
                      connectedCaseNumber: '',
                      connectedAgdFileRefAvailable: false,
                      connectedAgdFileRef: '' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.no}</span>
              </label>
            </div>
            <FieldError message={errors.hasConnectedMatters} />
          </div>
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <>
            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <label>
                  {f.connectedCaseNumber} <span className="req">*</span>
                </label>
              </div>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ marginBottom: 0 }}>
                    {f.connectedAgdFileRef} <span className="req">*</span>
                  </label>
                  <input
                    type="checkbox"
                    style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer', flexShrink: 0 }}
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setDraft((d) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                      }));

                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>Available</span>
                  {/* <label style={{ marginBottom: 0 }}>Available</label> */}
                </div>
              </div>
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg">
                <input
                  id="crim-connectedCase"
                  className={inpClass(Boolean(errors.connectedCaseNumber))}
                  type="text"
                  maxLength={MAX_REF}
                  value={draft.connectedCaseNumber}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedCaseNumber');
                  }}
                  placeholder={f.enterConnectedCaseNumber}
                />
                <FieldError message={errors.connectedCaseNumber} />
              </div>
              <div className="ccr-fg">
                <input
                  id="crim-connectedAgd"
                  className={inpClass(Boolean(errors.connectedAgdFileRef))}
                  type="text"
                  maxLength={MAX_REF}
                  disabled={!draft.connectedAgdFileRefAvailable}
                  style={
                    !draft.connectedAgdFileRefAvailable
                      ? {
                          cursor: 'default',
                          pointerEvents: 'none',
                        }
                      : undefined
                  }
                  value={draft.connectedAgdFileRef}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedAgdFileRef');
                  }}
                  placeholder={f.enterConnectedAgdFileRef}
                />
                <div className="ccr-slbl">
                  If you are unaware of the relevant Connected AGD File Reference Number details, please leave the
                  'Available' checkbox unchecked.
                </div>
                <FieldError message={errors.connectedAgdFileRef} />
              </div>
            </div>
          </>
        ) : null}
      </SectionCard>

      <CriminalWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </CriminalWizardShell>
  );
}

